import { Component, inject, Input, OnInit } from '@angular/core';
import { heightAnimation } from '@modules/common/animations/height.animation';
import { BaseSidebarContainersItemComponent } from '@modules/common/components/base-sidebar-containers-item/base-sidebar-containers-item.component';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { Application } from '@modules/common/types/application';
import { SidebarSplitViewKey } from '@modules/common/types/sidebar-split-view-key';
import { NotebookListStateService } from '@modules/notes/services/notebook-list-state.service';
import { Note } from '@modules/notes/types/note';
import { Notebook } from '@modules/notes/types/notebook';
import { type NotesRailScope } from '@modules/notes/components/sidebar-context-menu/notes-rail-menu';
import { NotebooksFilters } from '@modules/notes/types/notebooks-filters';
import { VirtualFolder } from '@modules/notes/types/virtual-folder';
import { StateKey } from '@modules/settings/types/state-key';
import { combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-side-bar-notebooks',
  templateUrl: './side-bar-notebooks.component.html',
  styleUrls: [
    '../../../common/components/base-sidebar-containers-item/base-sidebar-containers-item.component.less',
    './side-bar-notebooks.component.less',
  ],
  animations: [heightAnimation],
  standalone: false,
  providers: [
    ...NotebookListStateService.providers({
      stateKey: StateKey.sidebarNotebooksListState,
    }),
  ],
})
export class SideBarNotebooksComponent
  extends BaseSidebarContainersItemComponent<Notebook, Note, NotebooksFilters, VirtualFolder>
  implements OnInit
{
  /** `Notebooks` under My Workspace, `Team Notebooks` under the team's — the frame's two labels. */
  @Input() public title = 'Notebooks';
  /** Overrides the depth the base derives from `team`, so a section can indent its own tree. */
  @Input() public depth?: number;
  /** Passed straight to the tree — see its `tenantId` for why both sections need one (#1016). */
  @Input() public tenantId: string | null | undefined;
  /** A workspace section of the rail: the header gets the prototype menu, an options button and a semantic plus. */
  @Input() public rail = false;
  /** Which workspace the section is: the member's My Workspace or the Team Workspace. */
  @Input() public railScope: NotesRailScope = 'mine';

  application = Application.notes;
  sidebarSplitViewKey = SidebarSplitViewKey.notes;

  protected get railWorkspace(): string {
    return this.railScope === 'team' ? 'Team' : 'My';
  }

  private readonly notebookListStateService = inject(NotebookListStateService, { self: true });
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  public ngOnInit() {
    super.ngOnInit();

    combineLatest([this.notebookListStateService.value(), this.displayTimeZoneService.resolvedTimeZone$])
      .pipe(takeUntil(this.alive))
      .subscribe(([state, displayTimeZone]) => {
        // `displayTimeZone` FIRST: applyListState reads it to reckon its day windows in the account
        // zone (SBUX-829).
        this.filters = new NotebooksFilters({ displayTimeZone }).applyListState(state);
      });
  }
}
